import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { AngularFireStorage } from '@angular/fire/compat/storage';
import { Timestamp } from 'firebase/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import {
  MediaType,
  Picture,
  Resource,
  ResourceCategory,
  ResourceProps,
  ResourceStatus,
} from '../domain/resource';
import { ResourceNotFoundException } from '../domain/resource-exceptions';

export interface PictureSchema {
  url: string;
  path: string;
  createdAt: Timestamp;
  mimeType: string;
}

export interface ResourceSchema {
  title: string | null;
  picture: PictureSchema | null;
  date: Timestamp | null;
  content: string;
  credits: string | null;
  mediaUrl: string;
  mediaType: MediaType;
  status: ResourceStatus;
  category: ResourceCategory;
  createdAt: Timestamp;
  updatedAt: Timestamp | null;
}

@Injectable()
export class ResourceRepository {
  constructor(
    private firestore: AngularFirestore,
    private storage: AngularFireStorage,
  ) {
    // Nada
  }

  private collection(queryFn?: QueryFn) {
    return this.firestore.collection<ResourceSchema>('resources', queryFn);
  }

  toSchema(entity: Resource): ResourceSchema {
    return <ResourceSchema>{
      title: entity.title ?? null,
      picture: entity.picture ? this.toPictureSchema(entity.picture) : null,
      date: entity.date ? Timestamp.fromDate(entity.date) : null,
      content: entity.content,
      credits: entity.credits ?? null,
      mediaUrl: entity.mediaUrl ?? null,
      mediaType: entity.mediaType ?? null,
      status: entity.status,
      category: entity.category,
      createdAt: Timestamp.fromDate(entity.createdAt),
      updatedAt: entity.updatedAt ? Timestamp.fromDate(entity.updatedAt) : null,
    };
  }

  toPictureSchema(entity: Picture): PictureSchema {
    return <PictureSchema>{
      url: entity.url,
      path: entity.path,
      mimeType: entity.mimeType,
      createdAt: entity.createdAt
        ? Timestamp.fromDate(entity.createdAt)
        : Timestamp.now(),
    };
  }

  fromSchema(schema: ResourceSchema, id: string): Resource {
    return Resource.create(
      {
        title: schema.title ?? undefined,
        picture: schema.picture
          ? this.fromPictureSchema(schema.picture)
          : undefined,
        date: schema.date ? schema.date.toDate() : undefined,
        content: schema.content,
        credits: schema.credits ?? undefined,
        mediaUrl: schema.mediaUrl ?? undefined,
        mediaType: schema.mediaType ?? undefined,
        status: schema.status,
        category: schema.category,
        createdAt: schema.createdAt.toDate(),
        updatedAt: schema.updatedAt ? schema.updatedAt.toDate() : undefined,
      } as ResourceProps,
      new UniqueEntityID(id),
    );
  }

  fromPictureSchema(schema: PictureSchema): Picture {
    return <Picture>{
      url: schema.url,
      path: schema.path,
      mimeType: schema.mimeType,
      createdAt: schema.createdAt.toDate(),
    };
  }

  async findAll(
    filter: {
      category: ResourceCategory | undefined;
      status: ResourceStatus | undefined;
    },
    lastId: string | undefined,
  ): Promise<Resource[]> {
    try {
      if (lastId) {
        const last = await firstValueFrom(this.collection().doc(lastId).get());
        const snap = await firstValueFrom(
          this.collection((ref) => {
            if (filter.category) {
              if (filter.status) {
                return ref
                  .where('category', '==', filter.category)
                  .where('status', '==', filter.status)
                  .orderBy('date', 'desc')
                  .startAfter(last)
                  .limit(30);
              } else {
                return ref
                  .where('category', '==', filter.category)
                  .orderBy('date', 'desc')
                  .startAfter(last)
                  .limit(30);
              }
            } else {
              if (filter.status) {
                return ref
                  .where('status', '==', filter.status)
                  .orderBy('date', 'desc')
                  .startAfter(last)
                  .limit(30);
              } else {
                return ref.orderBy('date', 'desc').startAfter(last).limit(30);
              }
            }
          }).get(),
        );
        return snap.docs.map((doc) =>
          this.fromSchema(doc.data() as ResourceSchema, doc.id),
        );
      } else {
        const snap = await firstValueFrom(
          this.collection((ref) => {
            if (filter.category) {
              if (filter.status) {
                return ref
                  .where('category', '==', filter.category)
                  .where('status', '==', filter.status)
                  .orderBy('date', 'desc')
                  .limit(30);
              } else {
                return ref
                  .where('category', '==', filter.category)
                  .orderBy('date', 'desc')
                  .limit(30);
              }
            } else {
              if (filter.status) {
                return ref
                  .where('status', '==', filter.status)
                  .orderBy('date', 'desc')
                  .limit(30);
              } else {
                return ref.orderBy('date', 'desc').limit(30);
              }
            }
          }).get(),
        );
        return snap.docs.map((doc) =>
          this.fromSchema(doc.data() as ResourceSchema, doc.id),
        );
      }
    } catch (e: any) {
      console.log('🔴 ResourceRepository.findAll : ', e);
      throw new Error(e.message);
    }
  }

  async findAllByCategory(categoryId: string): Promise<Resource[]> {
    try {
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', 'active')
            .where('category', '==', categoryId),
        ).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as ResourceSchema, doc.id),
      );
    } catch (e) {
      console.log('🔴 ResourceRepository.findAllByCategory', e);
      return Promise.reject(e);
    }
  }

  async findAllUpcoming(lastId: string | undefined): Promise<Resource[]> {
    if (lastId) {
      const last = await firstValueFrom(this.collection().doc(lastId).get());
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', 'active')
            .where('date', '!=', null)
            .startAfter(last)
            .limit(30),
        ).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as ResourceSchema, doc.id),
      );
    } else {
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', 'active')
            .where('date', '!=', null)
            .limit(30),
        ).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as ResourceSchema, doc.id),
      );
    }
  }

  async findAllReplay(lastId: string | undefined): Promise<Resource[]> {
    if (lastId) {
      const last = await firstValueFrom(this.collection().doc(lastId).get());
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', 'active')
            .where('date', '==', null)
            .startAfter(last)
            .limit(30),
        ).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as ResourceSchema, doc.id),
      );
    } else {
      const snap = await firstValueFrom(
        this.collection((ref) =>
          ref
            .where('status', '==', 'active')
            .where('date', '==', null)
            .limit(30),
        ).get(),
      );
      return snap.docs.map((doc) =>
        this.fromSchema(doc.data() as ResourceSchema, doc.id),
      );
    }
  }

  async load(resourceId: string): Promise<Resource> {
    const snap = await firstValueFrom(this.collection().doc(resourceId).get());
    if (!snap.exists || snap.data() == null) {
      throw new ResourceNotFoundException();
    }
    const schema = snap.data() as ResourceSchema;
    return this.fromSchema(schema, snap.id);
  }

  async create(resource: Resource): Promise<Resource> {
    try {
      const schema = this.toSchema(resource);
      const ref = await this.collection().add(schema);
      return this.fromSchema(schema, ref.id);
    } catch (e: any) {
      console.log('🔴 ResourceRepository.create', e);
      throw new Error(e);
    }
  }

  async save(resource: Resource): Promise<Resource> {
    const schema = this.toSchema(resource);
    await this.collection().doc(resource.id.toString()).set(schema);
    return this.fromSchema(schema, resource.id.toString());
  }

  async savePicture(filename: string, base64Image: string) {
    const path = `resources/pictures/${Date.now()}_${filename}`;
    try {
      const uploadTask = await this.storage
        .ref(path)
        .putString(base64Image.split(',')[1], 'base64', {
          contentType: 'image/*',
        });
      return {
        path,
        url: await uploadTask.ref.getDownloadURL(),
      };
    } catch (e) {
      console.error(e);
      return Promise.reject(e);
    }
  }

  async deletePicture(path: string) {
    try {
      const ref = this.storage.ref(path);
      ref.delete().subscribe(
        (ev) => console.log('☢️ delete()', ev),
        (error) => console.log(error),
      );
    } catch (e) {
      console.error('🔴 deletePicture', e);
    }
  }
}
